import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CardBodyComponent, CardComponent, LoadingButtonComponent, StateMessageComponent } from "@groundworks/ngx-groundworks-ui";
import { DateTime } from 'luxon';
import { CreateSalesTouchpointRequest, SalesTouchpointInfo } from '../../sdk/sdk';
import { ModalService } from '../../services/modal.service';
import { SalesTouchpointService } from '../../services/sales-touchpoint.service';
@Component({
  selector: 'app-sales-touchpoints',
  imports: [
    CardBodyComponent,
    CardComponent,
    StateMessageComponent,
    LoadingButtonComponent,
  ],
  templateUrl: './sales-touchpoints.component.html',
})
export class SalesTouchpointsComponent {
  private readonly salesTouchpointService = inject(SalesTouchpointService);
  private readonly modalService = inject(ModalService);
  readonly touchPointsInput = input<SalesTouchpointInfo[]>([]);
  readonly isLoadingInput = input<boolean>(false);
  readonly hasErrorInput = input<boolean>(false);
  private readonly _touchPoints = signal<SalesTouchpointInfo[]>([]);
  readonly touchPoints = computed(() => this._touchPoints());
  readonly opportunityId = input<string>();
  readonly isAddingTouchpoint = signal(false);
  readonly touchpointCreated = output<void>();

  readonly state = computed(() => {
    if (this.isLoadingInput()) {
      return 'loading';
    }
    if (this.hasErrorInput()) {
      return 'error';
    }
    if (this.touchPoints().length === 0) {
      return 'no-matches';
    }
    return 'matches';
  });


  getState(): string {
    return this.state();
  }

  constructor() {
    effect(() => {
      this._touchPoints.set(this.touchPointsInput());
    });
  }

  getTimeAgo(date: Date): string {
    const dt = DateTime.utc(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds());
    const relative = dt.toRelative();

    if (relative === '0 seconds ago') {
      return 'just now';
    }

    return relative || 'just now';
  }

  onAddTouchpoint() {
    const noteElement = document.getElementById('sales-touchpoint-note') as HTMLTextAreaElement;
    const note = noteElement?.value?.trim();
    if (note && note.length > 0) {
      const request = new CreateSalesTouchpointRequest({
        opportunityId: this.opportunityId()!,
        note: note,
      });
      this.isAddingTouchpoint.set(true);
      this.salesTouchpointService.createSalesTouchpoint(request).subscribe({
        next: (touchpoint) => {
          const utcIsoString = DateTime.utc().toISO();
          touchpoint.createdAt = new Date(utcIsoString?.replace('Z', '') || '');
          this._touchPoints.set([touchpoint, ...this.touchPoints()]);
          this.isAddingTouchpoint.set(false);
          // Clear the box only on success, so a failed save doesn't silently lose the note.
          noteElement.value = '';
        },
        error: (error) => {
          console.error('Error creating sales touchpoint:', error);
          this.isAddingTouchpoint.set(false);
          // Tell the user the note wasn't saved and leave it in the box to retry (MOD-5534).
          this.modalService.openErrorModal(
            'Error Adding Note',
            this.getErrorMessage(error, 'An unexpected error occurred while adding the note. Please try again.'),
          );
        },
      });
    }
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    const err = error as { status?: number; error?: { detail?: string }; message?: string };
    const detail = err?.error?.detail;
    switch (err?.status) {
      case 403:
        return 'The current logged in user is not allowed to perform this action.';
      case 503:
        return detail || 'Notes are temporarily unavailable. Please try again later.';
      case 500:
        return detail || 'An internal server error occurred.';
      default:
        return detail || err?.message || fallback;
    }
  }
}
